import React, { useEffect, useState } from 'react'
import clsx from 'clsx'
import { Link, useParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from 'state-manager/store'

// components
import MoneySlider from 'pages/Subscriptions/MoneySlider'
import SubscriptionPlanCard from 'pages/Subscriptions/SubscriptionPlanCard'
import SpriteIcon from 'components/ui/SpriteIcon'
import CustomSubscriptionForm from 'pages/Subscriptions/CustomSubscriptionForm'
import ModalCoupon from 'pages/Subscriptions/ModalCoupon'
import Coupon from 'components/ui/Coupon/Coupon'
import CurrencyToggle from 'components/Onboarding/CurrencyToggle/CurrencyToggle'
import BulletList from 'components/Onboarding/BulletList/BulletList'

// actions
import {
  getAllPlans,
  createPlanRequest,
  getPlanByToken,
} from 'state-manager/actions/plans'
import { setCurrentSubscription } from 'state-manager/actions/subscriptions'
import { getUserCoupon } from 'state-manager/actions/coupon'
import { fetchSubscription } from 'state-manager/reducers/subscription'
import { fetchBillingInfo } from 'state-manager/reducers/billing-info'

// hooks
import _useToggle from 'hooks/use-toggle'

// helpers
import getCurrency from 'helpers/get-currency'

// routes
import routes from 'routes'

// constants
import { PERIOD_MONTH, PERIOD_YEAR } from 'constants/plans'
import {CURRENCIES, ICurrency} from 'constants/currencies'

// styles
import classes from 'pages/Subscriptions/Subscriptions.module.scss'

type CustomPlanType = {
  currency: number
  id: string
  monthlyMediaSpend: number
  period: string
  plan: string
  price: number
}

const Subscriptions = () => {
  const {
    coupon,
    allPlans,
    configCurrencies,
    currentSubscription,
    billingInfo,
  } = useAppSelector((state) => ({
    coupon: state.coupon,
    allPlans: state.plans.data,
    configCurrencies: state.currencies.data,
    currentSubscription: state.subscriptions.data,
    billingInfo: state.billingInfo.data,
  }))

  const dispatch = useAppDispatch()

  const params = useParams<{ token?: string }>()

  const [customPlanId, setCustomPlanId] = useState<CustomPlanType | null>(null)
  const [isModalOpen, toggleIsModalOpen] = _useToggle()
  const [isCouponModalOpen, toggleCouponModalOpen] = _useToggle()
  const [currentSubscriptionType, setCurrentSubscriptionType] = useState(
    currentSubscription?.plan?.period || ''
  )
  const getCurrencyById = (id: number): ICurrency | undefined => {
    return Object.values(CURRENCIES).find((currency) => currency.id === id)
  }

  let defaultCurrency: ICurrency = CURRENCIES.USD // fallback
  if (currentSubscription?.currency) {
    const currencyById = getCurrencyById(currentSubscription.currency)
    if (currencyById) {
      defaultCurrency = currencyById
    }
  }
  
  const [currency, setCurrency] = useState<ICurrency>(defaultCurrency)

  const [sliderValue, setSliderValue] = useState(currentSubscription.plan?.monthlyMediaSpend || 25000)

  useEffect(() => {
    if (!currentSubscription.renewsAt) {
      dispatch(fetchSubscription())
    }
    if (!allPlans.length) {
      dispatch(getAllPlans())
    }
    if (!billingInfo?.cardLastFour) {
      dispatch(fetchBillingInfo())
    }
    dispatch(getUserCoupon()).catch(() => {})
    //eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (params.token && !customPlanId) {
      dispatch(getPlanByToken(params.token)).then((res) => {
        setCustomPlanId(res)
      })
    }
    //eslint-disable-next-line
  }, [])

  if (!allPlans.length) {
    return null
  }

  const monthly = allPlans.filter((item) => item.period === PERIOD_MONTH)
  const yearly = allPlans.filter((item) => item.period === PERIOD_YEAR)

  const monthlyPlan = allPlans.find(
    (item) =>
      item.monthlyMediaSpend === sliderValue &&
      item.currency === currency.id &&
      item.period === PERIOD_MONTH
  )
  const yearlyPlan = allPlans.find(
    (item) =>
      item.monthlyMediaSpend === sliderValue &&
      item.currency === currency.id &&
      item.period === PERIOD_YEAR
  )
  const maxSliderValue = 4000000

  const isLimitReached = sliderValue === maxSliderValue

  const handleSubscribe = (planId: number, type?: string) => {
    const formData: {
      planId: number
      token?: string
      coupon?: string
    } = {
      planId: planId,
    }

    if (customPlanId) {
      formData.token = params.token
    } else if (coupon?.id) {
      formData.coupon = coupon.id
    }

    if (type) {
      setCurrentSubscriptionType(type)
    }

    if (isLimitReached) {
      toggleIsModalOpen()
      return
    }

    dispatch(setCurrentSubscription(formData))
  }

  if (currentSubscription.hasCustomPlanRequest) {
    return (
      <div
        className={clsx(
          classes.customRequestPendingText,
          'd-flex align-items-center justify-content-center fw-semibold fs-lg color-green'
        )}>
        Your request is pending. You will be contacted shortly
      </div>
    )
  }

  const isCouponApplied = Boolean(coupon?.percentOff || coupon?.amountOff)
  const couponMultiplier = coupon?.percentOff ? (100 - coupon?.percentOff) / 100 : 1
  const couponAmount = coupon?.amountOff ? coupon.amountOff / 100 : 0

  // divided by 100 because finalMonthlyPrice is in pennies
  const monthlyPrice = parseInt(`${monthlyPlan ? monthlyPlan.price / 100 : 0}`)
  const yearlyPrice = parseInt(`${yearlyPlan ? yearlyPlan.price / 100 : 0}`)

  const monthlyPriceWithDiscount = coupon?.percentOff
      ? parseInt(`${monthlyPrice * couponMultiplier}`)
      : Math.max(0, parseInt(`${monthlyPrice - couponAmount}`))

  const yearlyPriceWithDiscount = coupon?.percentOff
      ? parseInt(`${yearlyPrice * couponMultiplier}`)
      : Math.max(0, parseInt(`${yearlyPrice - couponAmount}`))

  // divided by 100 because finalMonthlyPrice is in pennies and by 4 because we need to calculate 20% discount from original finalMonthlyPrice
  const yearlyProfit = !customPlanId
    ? parseFloat(
        `${
          isCouponApplied
            ? monthlyPrice * 12 - yearlyPriceWithDiscount
            : monthlyPrice * 12 - yearlyPrice
        }`
      )
    : ''

  const yearlyPriceByMonth = parseInt(`${yearlyPrice / 12}`)
  const yearlyPriceWithDiscountByMonth = parseInt(
    `${yearlyPriceWithDiscount / 12}`
  )

  const handleCurrencyChange = (currency: ICurrency) => {
    setCurrency(currency)
  }

  return (
    <div>
      <div className="d-flex justify-content-center mt-10 position-relative">
        {currentSubscription?.renewsAt && (
          <Link to={routes.invoices}>
            <SpriteIcon name="back" size="md" className={classes.closeIcon} />
          </Link>
        )}
        <div className="container text-center">
          <h1 className="fw-regular">Choose Your Plan</h1>
          <p
            className={clsx(
              classes.descriptionMessage,
              'fw-regular fw-semibold color-black m-auto'
            )}>
            Move the slider to your new required media spend level to update your plan.
          </p>
          {customPlanId ? (
            <div className="mt-10 d-flex justify-content-center align-items-center">
              <div className="fw-regular fs-18 mr-3">Chosen plan:</div>
              <div className="fw-medium fs-20">
                {customPlanId
                  ? customPlanId.monthlyMediaSpend.toLocaleString()
                  : 0}{' '}
                {getCurrency(customPlanId?.currency, configCurrencies)}
              </div>
            </div>
          ) : (
            <>
              <div
                className='d-flex align-items-center mt-5 flex-column-sm-up justify-content-center'>
                <div className={clsx(classes.slider, 'jmb-sm-up-8')}>
                  <MoneySlider
                    value={sliderValue}
                    handleSliderChange={setSliderValue}
                    currency={currency.symbol}
                    handleStyle={{
                      marginTop: '-8px',
                      width: '27px',
                      height: '27px',
                      borderWidth: '2px',
                      borderColor: '#3DB14A',
                      borderRadius: 13.5,
                      backgroundColor: 'white',
                      padding: 0,
                      boxShadow: '0 1px 1px rgba(40,47,82,0.5), inset 0 0 0 4px white',
                    }}
                    trackStyle={{
                      backgroundColor: '#3DB14A',
                      height: '13px'
                    }}
                    railStyle={{
                      backgroundColor: '#F5F5F5',
                      height: '13px'
                    }}
                    showSpendText={false}
                  />
                </div>
                {!currentSubscription?.plan && (
                  <div className={clsx(classes.currencyToggle)}>
                    <CurrencyToggle value={currency} onChange={handleCurrencyChange}/>
                  </div>
                )}
              </div>
            </>
          )}
          {sliderValue && (
            <div
              className={clsx(
                classes.planCardContainer,
                'd-flex justify-content-center'
              )}>
              {Boolean(monthly.length) && !customPlanId && (
                <div className={clsx(classes.planCard)}>
                  <SubscriptionPlanCard
                    isCouponApplied={isCouponApplied}
                    isFirstSubscription={!currentSubscription?.renewsAt}
                    title="Monthly"
                    currency={currency.symbol}
                    isLimitReached={isLimitReached}
                    subscribeOnPlan={(type) => {
                      if (isLimitReached) {
                        toggleIsModalOpen()
                      } else if (monthlyPlan) {
                        handleSubscribe(monthlyPlan.id, type)
                      }
                    }}
                    yearlyPrice={yearlyPrice}
                    yearlyPriceWithDiscount={yearlyPriceWithDiscount}
                    monthlyPrice={monthlyPrice}
                    monthlyPriceWithDiscount={monthlyPriceWithDiscount}
                    yearlyProfit={yearlyProfit}
                    yearlyPriceByMonth={yearlyPriceByMonth}
                    yearlyPriceWithDiscountByMonth={yearlyPriceWithDiscountByMonth}
                  />
                </div>
              )}
              {customPlanId?.period === PERIOD_MONTH && (
                <div className={clsx(classes.planCard)}>
                  <SubscriptionPlanCard
                    isFirstSubscription={!currentSubscription?.renewsAt}
                    title="Monthly"
                    currency={currency.symbol}
                    isLimitReached={false}
                    subscribeOnPlan={(type) => {
                      if (isLimitReached) {
                        toggleIsModalOpen()
                      } else if (monthlyPlan) {
                        handleSubscribe(monthlyPlan.id, type)
                      }
                    }}
                    yearlyPrice={0}
                    yearlyPriceWithDiscount={0}
                    monthlyPrice={0}
                    monthlyPriceWithDiscount={customPlanId.price / 100}
                    yearlyProfit={0}
                    yearlyPriceByMonth={0}
                    yearlyPriceWithDiscountByMonth={0}
                  />
                </div>
              )}
              {Boolean(yearly.length) && !customPlanId && (
                <div className={clsx(classes.planCard)}>
                  <SubscriptionPlanCard
                    isCouponApplied={isCouponApplied}
                    isFirstSubscription={!currentSubscription?.renewsAt}
                    title="Yearly"
                    isYearlyCard={!customPlanId}
                    currency={currency.symbol}
                    isLimitReached={isLimitReached}
                    subscribeOnPlan={(type) => {
                      if (isLimitReached) {
                        toggleIsModalOpen()
                      } else if (yearlyPlan) {
                        handleSubscribe(yearlyPlan.id, type)
                      }
                    }}
                    yearlyPrice={yearlyPrice}
                    yearlyPriceWithDiscount={yearlyPriceWithDiscount}
                    monthlyPrice={monthlyPrice}
                    monthlyPriceWithDiscount={monthlyPriceWithDiscount}
                    yearlyProfit={yearlyProfit}
                    yearlyPriceByMonth={yearlyPriceByMonth}
                    yearlyPriceWithDiscountByMonth={yearlyPriceWithDiscountByMonth}
                  />
                </div>
              )}
              {customPlanId?.period === PERIOD_YEAR && (
                <div className={clsx(classes.planCard, classes.planCardFirst)}>
                  <SubscriptionPlanCard
                    isFirstSubscription={!currentSubscription?.renewsAt}
                    title="Yearly"
                    currency={currency.symbol}
                    isLimitReached={isLimitReached}
                    subscribeOnPlan={(type) => {
                      if (isLimitReached) {
                        toggleIsModalOpen()
                      } else if (yearlyPlan) {
                        handleSubscribe(yearlyPlan.id, type)
                      }
                    }}
                    yearlyPrice={0}
                    yearlyPriceWithDiscount={0}
                    monthlyPrice={0}
                    monthlyPriceWithDiscount={Number(
                      customPlanId.price / 100 / 12
                    ).toFixed(2)}
                    yearlyProfit={0}
                    yearlyPriceByMonth={0}
                    yearlyPriceWithDiscountByMonth={0}
                  />
                </div>
              )}
            </div>
          )}
          <div className={clsx(classes.featuresCardContainer)}>
            <div className={clsx(classes.featuresCard)}>
              <BulletList
                features={[
                  'Budget Pacing Reporting',
                  'Automated Budget Management',
                ]}
              />
            </div>
            <div className={clsx(classes.featuresCard)}>
              <BulletList
                features={[
                  'Unlimited Ad Accounts & Users',
                  'Email & In-App Alerts',
                ]}
              />
            </div>
          </div>
          {!customPlanId && (
            <div className="my-2">
              <Coupon
                onClick={toggleCouponModalOpen}
                currency={currency.symbol}
                monthlyPrice={monthlyPrice}
                monthlyPriceWithDiscount={monthlyPriceWithDiscount}
              />
            </div>
          )}
          <div className='d-flex justify-content-center mt-2 mb-2'>
            <p className="text-italic p-2">
              PRICES SHOWN EXCLUDE UK VAT WHICH WILL BE ADDED AT THE PREVAILING RATE ONLY FOR CUSTOMERS IN THE UNITED
              KINGDOM
            </p>
          </div>
        </div>
      </div>
      {isCouponModalOpen && <ModalCoupon onClose={toggleCouponModalOpen}/>}
      {isModalOpen && (
        <CustomSubscriptionForm
          currentSubscriptionType={currentSubscriptionType}
          handlePlanRequest={(data) => {
            dispatch(createPlanRequest(data)).then(toggleIsModalOpen)
          }}
          onClose={toggleIsModalOpen}
        />
      )}
    </div>
  )
}

export default Subscriptions
